import {
	AccessCodeChange,
	AppArgs,
	Box,
	BoxFeature,
	CubeUtilization,
	Device,
	DeviceListener,
	EndOccupancyOptions,
	LanguageListener,
	LauncherItem,
	LocationCodeListener,
	LockStatus,
	LockStatusListener,
	LogLevel,
	Occupancy,
	OccupancyContent,
	OccupancyListener,
	OccupyRequest,
	Platform,
	StorageListener,
	UtilizationListener,
} from "@variocube/app-api";
import {findBoxByNumber, naturalCompare} from "@variocube/app-common";
import {computeCubeUtilization} from "@variocube/app-common";
import {BoxListener, Controller} from "./controller/Controller";
import {safeInvokeListeners} from "./safeInvokeListeners";

export interface AppHostBridge {
	language: string;
	setLauncherItems(appId: string, items: LauncherItem[]): void;
	addLanguageListener(listener: LanguageListener): void;
	removeLanguageListener(listener: LanguageListener): void;
}

export class AppPlatform implements Platform {
	constructor(
		readonly appId: string,
		private readonly controller: Controller,
		private readonly args: AppArgs,
		public readonly secondary: boolean,
		private readonly appHostBridge: AppHostBridge,
	) {
		controller.addStorageListener((appId: string, key: string) => {
			if (appId == this.appId) {
				safeInvokeListeners(this.storageListeners, key);
			}
		});

		controller.addBoxListener(boxes => {
			this._boxes = this.filterAndSortBoxes(boxes);
			safeInvokeListeners(this.boxListeners, this._boxes);
			this.updateUtilization();
		});

		controller.addOccupancyListener({
			accessChanged: _ => {},
			created: occupancy => {
				if (occupancy.appId == this.appId) {
					this._occupancies = [...this._occupancies, occupancy];
					this.notifyOccupancyListeners();
				}
				this.allOccupancies.push(occupancy);
				this.updateUtilization();
			},
			updated: occupancy => {
				if (occupancy.appId == this.appId) {
					this._occupancies = this._occupancies.map(existing => {
						return existing.uuid == occupancy.uuid ? occupancy : existing;
					});
					this.notifyOccupancyListeners();
				}
			},
			ended: uuid => {
				const index = this._occupancies.findIndex(occupancy => occupancy.uuid == uuid);
				if (index >= 0) {
					this._occupancies = this._occupancies.filter(occupancy => occupancy.uuid != uuid);
					this.notifyOccupancyListeners();
				}
				this.allOccupancies = this.allOccupancies.filter(occupancy => occupancy.uuid != uuid);
				this.updateUtilization();
			},
		});

		controller.addDeviceListener(devices => {
			this._devices = devices;
			safeInvokeListeners(this.deviceListeners, devices);
		});
	}

	async load() {
		this._boxes = this.filterAndSortBoxes(await this.controller.getBoxes());
		this._occupancies = await this.controller.getOccupancies(this.appId);
		this.allOccupancies = await this.controller.getOccupancies();
		this._devices = await this.controller.getDevices();
		this.updateUtilization();
	}

	private _boxes: Box[] = [];

	get boxes() {
		return this._boxes;
	}

	private _occupancies: Occupancy[] = [];
	get occupancies() {
		return this._occupancies;
	}

	private _utilization: CubeUtilization = {
		all: {
			available: 0,
			disabled: 0,
			occupied: 0,
			total: 0,
		},
		types: {},
		cooled: {},
		accessible: {},
		dangerousGoods: {},
		charger: {},
	};

	get utilization() {
		return this._utilization;
	}

	private updateUtilization() {
		this._utilization = computeCubeUtilization(this._boxes, this.allOccupancies);
		safeInvokeListeners(this.utilizationListeners, this._utilization);
	}

	get language() {
		return this.appHostBridge.language;
	}

	/* Array of all occupancies, used to calculate app-specific utilization. */
	private allOccupancies: Occupancy[] = [];

	private storageListeners = new Set<StorageListener>();
	private boxListeners = new Set<BoxListener>();
	private occupancyListeners = new Set<OccupancyListener>();
	private utilizationListeners = new Set<UtilizationListener>();
	private deviceListeners = new Set<DeviceListener>();

	get hostname() {
		return this.controller.hostname;
	}

	get nfcReader() {
		return this.controller.nfcReader;
	}

	get paymentTerminal() {
		return this.controller.paymentTerminal;
	}

	get locationCode() {
		return this.controller.locationCode;
	}

	get doorbell() {
		return this.controller.doorbell;
	}

	private _devices: Device[] = [];
	get devices() {
		return this._devices;
	}

	addLockStatusListener(listener: LockStatusListener) {
		this.controller.addLockStatusListener(listener);
	}

	removeLockStatusListener(listener: LockStatusListener) {
		this.controller.removeLockStatusListener(listener);
	}

	addLocationCodeListener(listener: LocationCodeListener) {
		this.controller.addLocationCodeListener(listener);
	}

	removeLocationCodeListener(listener: LocationCodeListener) {
		this.controller.removeLocationCodeListener(listener);
	}

	addUtilizationListener(listener: UtilizationListener) {
		this.utilizationListeners.add(listener);
	}

	removeUtilizationListener(listener: UtilizationListener) {
		this.utilizationListeners.delete(listener);
	}

	addStorageListener(listener: StorageListener) {
		this.storageListeners.add(listener);
	}

	removeStorageListener(listener: StorageListener) {
		this.storageListeners.delete(listener);
	}

	addBoxListener(listener: BoxListener) {
		this.boxListeners.add(listener);
	}

	removeBoxListener(listener: BoxListener) {
		this.boxListeners.delete(listener);
	}

	addOccupancyListener(listener: OccupancyListener) {
		this.occupancyListeners.add(listener);
	}

	removeOccupancyListener(listener: OccupancyListener) {
		this.occupancyListeners.delete(listener);
	}

	addDeviceListener(listener: DeviceListener) {
		this.controller.addDeviceListener(listener);
	}

	removeDeviceListener(listener: DeviceListener) {
		this.controller.removeDeviceListener(listener);
	}

	addLanguageListener(listener: LanguageListener) {
		this.appHostBridge.addLanguageListener(listener);
	}

	removeLanguageListener(listener: LanguageListener) {
		this.appHostBridge.removeLanguageListener(listener);
	}

	private notifyOccupancyListeners() {
		safeInvokeListeners(this.occupancyListeners, this._occupancies);
	}

	openLock(lockHandle: string) {
		return this.controller.open(lockHandle);
	}

	requireBoxMaintenance(boxNumber: string) {
		return this.controller.updateBoxMaintenanceState(boxNumber, true);
	}

	getStorageItem(key: string) {
		return this.controller.getStorageItem(this.appId, key);
	}

	getStorageItemBlob(key: string) {
		return this.controller.getStorageItemBlob(this.appId, key);
	}

	getArg(argName: string) {
		return this.args[argName];
	}

	occupy(request: OccupyRequest) {
		return this.controller.occupy({...request, appId: this.appId});
	}

	confirmOccupancy(occupancyId: string, content?: OccupancyContent, merge?: boolean) {
		return this.controller.confirmOccupancy(occupancyId, content, merge);
	}

	cancelOccupancy(occupancyId: string) {
		return this.controller.cancelOccupancy(occupancyId);
	}

	updateOccupancy(occupancyId: string, content: OccupancyContent, merge = false) {
		return this.controller.updateOccupancy(occupancyId, content, merge);
	}

	changeAccessCode(occupancyId: string, accessCode: string | AccessCodeChange) {
		return this.controller.changeAccessCode(occupancyId, accessCode);
	}

	changeAccessKeys(occupancyId: string, accessKeys: string[]) {
		return this.controller.changeAccessKeys(occupancyId, accessKeys);
	}

	getOccupancies(access?: string) {
		// if accessCode is an empty string, it would make the controller return all occupancies for the app
		if (typeof access == "string" && access.length == 0) {
			return Promise.resolve([]);
		}
		return this.controller.getOccupancies(this.appId, access);
	}

	getOccupancy(occupancyId: string) {
		return this.controller.getOccupancy(occupancyId);
	}

	endOccupancy(occupancyId: string, options?: EndOccupancyOptions) {
		return this.controller.endOccupancy(occupancyId, options);
	}

	async getLockStatus(lockHandle: string) {
		return (await this.controller.getStatus(lockHandle)) as LockStatus;
	}

	private filterAndSortBoxes(boxes: Box[]) {
		return boxes.filter(box => canAppUseBox(this.appId, box))
			.sort((a, b) => naturalCompare(a.number, b.number));
	}

	async getBoxes() {
		return this.filterAndSortBoxes(await this.controller.getBoxes());
	}

	getOccupiedBoxes() {
		return this.boxes
			.filter(box => this.allOccupancies.find(occupancy => occupancy.boxNumber == box.number));
	}

	async log(level: LogLevel, message: string, data: any) {
		return this.controller.log(level, this.appId, message, data);
	}

	async openBoxLock(boxNumber: string) {
		const lock = await this.getLock(boxNumber);
		if (!lock) {
			throw new Error("Could not determine lock");
		}
		await this.openLock(lock);
	}

	async getBoxLockStatus(boxNumber: string) {
		const lock = await this.getLock(boxNumber);
		if (!lock) {
			throw new Error("Could not determine lock");
		}
		return await this.getLockStatus(lock);
	}

	async getLock(boxNumber: string): Promise<string | undefined> {
		const box = await findBoxByNumber(this, boxNumber);
		return this.secondary ? box?.secondaryLock : box?.lock;
	}

	setLauncherItems(items: LauncherItem[]) {
		this.appHostBridge.setLauncherItems(this.appId, items);
	}

	computeCubeUtilization(features: BoxFeature[]) {
		return computeCubeUtilization(this._boxes, this.allOccupancies, features);
	}
}

function canAppUseBox(appId: string, box: Box) {
	if (box.types.indexOf("Master") >= 0 || box.types.indexOf("Barcode") >= 0) {
		return false;
	}
	const apps = box.apps || [];
	return Boolean(apps.length == 0 || apps.find(app => app == appId));
}
